import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { catchError, debounceTime, of, switchMap, tap } from 'rxjs';
import { ContactsService } from 'src/app/private/services/contacts.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { applyCenterScopeToWhere } from 'src/app/utils/tenant-scope.util';

@Component({
  selector: 'app-auto-complete-contact-search',
  templateUrl: './auto-complete-contact-search.component.html',
  styleUrls: ['./auto-complete-contact-search.component.scss']
})
export class AutoCompleteContactSearchComponent implements OnInit, OnChanges {
  searchControl = new FormControl('');
  @Input() label: string = 'Search Contacts';
  @Input() hideLabel = false;
  @Input() placeholder = 'Search';
  options: any[] = [];
  page = 0;
  readonly pageSize = 100;
  isLoading = false;
  selectedId: any = null;
  @Input() account: any;
  @Input() formAction: string = 'Add';
  @Input() selectedName: any = null;
  @Input() account_id: any = null;
  /** Match lead/contact/account modal form-control height and styling */
  @Input() modalField = false;
  isAdmin: boolean = false;

  private searchWired = false;

  constructor(
    private api: ContactsService,
    private localStorage: LocalStorageService
  ) {
    this.isAdmin = this.localStorage.checkFullAccessForTableList(
      this.localStorage.getRole()
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['account_id']) {
      return;
    }

    const previousId = changes['account_id'].previousValue;
    const currentId = changes['account_id'].currentValue;
    const accountChanged = previousId !== currentId;

    if (accountChanged && !changes['account_id'].firstChange) {
      // Reset contact when a different account is selected
      this.searchControl.setValue('', { emitEvent: false });
      this.selectedId = null;
      this.selectedName = null;
      this.page = 0;
    }

    if (currentId == null || currentId === '') {
      this.options = [];
      return;
    }

    this.page = 0;
    this.fetchData('').subscribe(data => {
      this.options = Array.isArray(data?.rows) ? data.rows : [];
      this.isLoading = false;
    });
  }

  ngOnInit() {
    if (this.formAction == 'Update' && this.account) {
      const contactName =
        this.account.account_name ||
        `${this.account.first_name || ''} ${this.account.last_name || ''}`.trim();
      if (contactName) {
        this.searchControl.setValue(contactName, { emitEvent: false });
      }
      this.selectedId = this.account.account_id || this.account.contact_id || null;
    }

    if (!this.searchWired) {
      this.searchWired = true;
      this.searchControl.valueChanges
        .pipe(
          debounceTime(400),
          tap(() => {
            this.page = 0;
            this.options = [];
          }),
          switchMap(value => {
            if (this.account_id == null || this.account_id === '') {
              return of({ rows: [] });
            }
            const query = typeof value === 'string' ? value.trim() : '';
            return this.fetchData(query);
          })
        )
        .subscribe(data => {
          this.options = Array.isArray(data?.rows) ? data.rows : [];
        });
    }
  }

  fetchData(query: any) {
    if (this.account_id == null || this.account_id === '') {
      this.isLoading = false;
      return of({ rows: [] });
    }

    this.isLoading = true;

    const where = applyCenterScopeToWhere(
      {
        client_id: this.localStorage.getClientId(),
        account_id: this.account_id,
      },
      this.localStorage
    );

    if (!this.isAdmin) {
      where['user_id'] = this.localStorage.getUserId();
    }

    const param: any = {
      page: this.page * this.pageSize,
      limit: this.pageSize,
      where,
      order: {
        col: '"contact"."first_name"',
        dir: 'ASC',
      },
    };

    const search = typeof query === 'string' ? query.trim() : '';
    if (search) {
      param.filter = {
        staticFilter: {
          first_name: search,
          last_name: search,
        },
      };
    }

    return this.api.getAllContacts(param).pipe(
      tap(() => (this.isLoading = false)),
      catchError((error) => {
        this.isLoading = false;
        console.error('Error fetching data:', error);
        return of({ rows: [] });
      })
    );
  }

  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    if (
      element.scrollTop + element.clientHeight >= element.scrollHeight &&
      !this.isLoading &&
      this.account_id != null &&
      this.account_id !== ''
    ) {
      this.page++;
      this.fetchData(this.searchControl.value).subscribe(data => {
        const rows = Array.isArray(data?.rows) ? data.rows : [];
        this.options = [...this.options, ...rows];
      });
    }
  }

  onSelectionChange(event: any) {
    this.selectedId = event?.contact_id;
  }
}
